import React, { useState, useEffect, useContext } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Axios from 'axios';
import { Helmet } from 'react-helmet-async';

import { NewLoginForm } from './NewLoginDailogForm';
import { BackDropLoad } from './LoadingBackDrop';
import { UserContext } from './userContext';
import { PageContainer } from '../theme/primitives';

const key = 'dqweqwe123y97qhduiasd$@^!*#&bjasdja&^&@!#';

function slugify(s) {
  return String(s || '').toLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '');
}

/* ─────────── Inline icons ─────────── */

const IconTrending = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
    <path d="m3 17 6-6 4 4 8-8" />
    <path d="M14 7h7v7" />
  </svg>
);

const IconCalendar = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
);

/* ─────────── Hero ─────────── */

function TrendsHero() {
  return (
    <section
      className="
        relative isolate overflow-hidden mt-3 mb-6
        rounded-2xl border border-border-subtle
        bg-gradient-to-br from-surface-paper to-surface-elevated
      "
    >
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10"
        style={{ background: 'radial-gradient(900px 400px at 10% -10%, rgba(251,191,36,0.16), transparent 60%), radial-gradient(700px 360px at 100% 110%, rgba(251,191,36,0.08), transparent 60%)' }}
      />
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 opacity-30"
        style={{
          backgroundImage:
            'linear-gradient(rgba(46,46,52,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(46,46,52,0.6) 1px, transparent 1px)',
          backgroundSize: '52px 52px',
          maskImage: 'radial-gradient(ellipse 65% 70% at 50% 50%, #000, transparent 85%)',
          WebkitMaskImage: 'radial-gradient(ellipse 65% 70% at 50% 50%, #000, transparent 85%)',
        }}
      />
      <div aria-hidden className="pointer-events-none absolute inset-x-12 top-0 h-px bg-gradient-to-r from-transparent via-amber/50 to-transparent" />

      <div className="px-5 sm:px-7 md:px-9 py-7 sm:py-8">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-amber/24 bg-amber/10 px-2.5 py-1">
          <span aria-hidden className="inline-flex h-1.5 w-1.5 rounded-full bg-amber" style={{ boxShadow: '0 0 10px rgba(251,191,36,0.7)' }} />
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber">Industry pulse</span>
        </div>
        <h1 className="text-[28px] sm:text-[34px] md:text-[40px] font-bold leading-[1.05] tracking-[-0.03em] text-fg-primary">
          Construction Trends
        </h1>
        <div aria-hidden className="mt-3.5 h-[2px] w-12 rounded-full bg-gradient-to-r from-amber to-amber/0" />
        <p className="mt-3.5 max-w-[640px] text-[14px] leading-[1.6] text-fg-secondary">
          Latest innovations, practices, and movements shaping the construction industry.
        </p>
      </div>
    </section>
  );
}

/* ─────────── Trend card ─────────── */

function TrendCard({ item }) {
  return (
    <RouterLink
      to={`/trends-list/trend/${slugify(item.blog_title)}/${item.blog_id}`}
      title={item.blog_title}
      className="
        group relative flex h-full flex-col overflow-hidden rounded-xl
        border border-border-subtle bg-surface-paper
        transition-[transform,border-color,box-shadow] duration-300 ease-out-soft
        hover:-translate-y-1 hover:border-amber hover:shadow-amber-glow-strong
      "
    >
      {/* Image */}
      <div className="relative aspect-[16/10] overflow-hidden bg-surface-elevated">
        <img
          src={item.blog_image}
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-out-soft group-hover:scale-[1.07]"
        />
        <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/40 to-transparent transition-opacity duration-500 ease-out-soft group-hover:opacity-60" />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 ease-out-soft group-hover:opacity-100"
          style={{ background: 'radial-gradient(ellipse 80% 50% at 50% 100%, rgba(251,191,36,0.16), transparent 70%)' }}
        />
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col gap-1.5 p-4">
        <div className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.18em] text-amber transition-colors duration-200 ease-out-soft group-hover:text-amber-light">
          <IconCalendar className="h-3 w-3 shrink-0" />
          <span>{item.added_date}</span>
        </div>

        <h3 className="text-[15px] font-semibold leading-tight tracking-[-0.01em] text-fg-primary line-clamp-3 transition-transform duration-300 ease-out-soft group-hover:-translate-y-0.5">
          {item.blog_title}
        </h3>

        <div className="mt-auto pt-2 flex items-center gap-2">
          <span
            aria-hidden
            className="block h-[2px] w-5 bg-amber transition-[width,background-color] duration-500 ease-out-soft group-hover:w-10 group-hover:bg-amber-light"
          />
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber opacity-0 -translate-x-1 transition-[opacity,transform] duration-300 ease-out-soft group-hover:opacity-100 group-hover:translate-x-0">
            Read
          </span>
        </div>
      </div>
    </RouterLink>
  );
}

/* ─────────── Empty state ─────────── */

function EmptyTrends() {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border-subtle bg-surface-paper/40 px-6 py-14 text-center">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-border-subtle bg-surface-overlay/60 text-fg-tertiary">
        <IconTrending className="h-6 w-6" />
      </div>
      <h3 className="text-[16px] font-semibold text-fg-primary">No trends published yet</h3>
      <p className="mt-1.5 max-w-[380px] text-[13px] leading-relaxed text-fg-secondary">
        Check back later for industry insights.
      </p>
    </div>
  );
}

/* ═══════════════════ Trends ═══════════════════ */

export const Trends = () => {
  const [loading, setLoading] = useState(true);
  const [trends, setTrends] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const { user } = useContext(UserContext);
  const [showLoginForm, setShowLoginForm] = useState(!user);
  const [wait, setWait] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    Axios.post('/new_apis/getsinglemetadata', { type: 'trends' }, { headers: { key } }).then((res) => {
      if (res.data.status) setMetadata(res.data.metadata[0]);
      else setMetadata({
        seo_title: 'Trends | ESAY',
        seo_desc: 'Latest trends and innovations in the construction industry.',
        seo_keywords: 'construction trends',
      });
    });
    Axios.get('/new_apis/get-trends', { headers: { key } }).then((res) => {
      setTrends(res.data.trends || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <>
      {metadata && (
        <Helmet>
          <title>{metadata.seo_title}</title>
          <meta name="description" content={metadata.seo_desc} />
          <meta name="keywords" content={metadata.seo_keywords} />
        </Helmet>
      )}

      <BackDropLoad wait={wait} />
      <NewLoginForm
        strict
        strictTitle="Sign in to view Trends"
        strictMessage="Trends are available to ESAY members. Log in to browse the latest articles and updates."
        open={showLoginForm}
        showload={() => setWait(true)}
        hideload={() => setWait(false)}
        closeform={() => setShowLoginForm(false)}
        openloginform={() => setShowLoginForm(true)}
      />

      <PageContainer maxWidth="lg">
        <TrendsHero />

        <section className="mt-2 mb-10">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="overflow-hidden rounded-xl border border-border-subtle bg-surface-paper"
                >
                  <div className="aspect-[16/10] animate-pulse bg-surface-overlay/60" />
                  <div className="p-4 flex flex-col gap-2">
                    <div className="h-2 w-16 rounded bg-surface-overlay/60 animate-pulse" />
                    <div className="h-3 w-3/4 rounded bg-surface-overlay/60 animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : trends.length === 0 ? (
            <EmptyTrends />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {trends.map((item) => (
                <TrendCard key={item.blog_id} item={item} />
              ))}
            </div>
          )}
        </section>
      </PageContainer>
    </>
  );
};
